/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo } from "react";
import { CheckCircleOutlined, UserOutlined } from "@ant-design/icons";
import { green } from "@ant-design/colors";
import {
	Table,
	Space,
	Button,
	Row,
	Col,
	Typography,
	Tooltip,
} from "antd";
import { connect, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { getPatients } from "modules/Session/reducers";
import { getCurrentLocation } from "modules/Location/reducers";
import * as actions from "modules/Session/actions";
import NewPatientForm from "./NewPatient";
import { trackEvent, trackButtonClick, setSessionContext } from "utils/analytics";

const { Text } = Typography;

function ExistingPatients({
	patients,
	location,
	account,
	practiceId,
	setCurrentPatient,
	currentPatient,
	clearPatients,
}) {
	const history = useHistory();
	const [newPatient, setNewPatient] = React.useState(false);

	const handleSetPatient = (record) => {
		setCurrentPatient(record);
		// Set patient ID in session context
		setSessionContext({ patientId: record.id });
		trackEvent("existing_patient_selected", {
			patient_id: record.id,
			patient_status: record.state,
			patients_available: patients?.length || 0,
		});
		trackButtonClick('select_existing_patient_button', { patient_id: record.id });
		history.push("confirm");
	};

	const currentPractice = useSelector(
		(state) => state.locations.currentLocation,
	);

	const themeColor = currentPractice?.themeColor;

	const pendingPatientAllowed = useMemo(
		() => currentPractice?.allowPendingPatients,
		[],
	);

	const themedGhostStyle = themeColor ? {
		color: themeColor,
		borderColor: themeColor,
	} : {};

	const themedButtonStyle = themeColor ? {
		backgroundColor: themeColor,
		borderColor: themeColor,
	} : {};

	const columns = [
		{
			title: "Name",
			dataIndex: "fullNameWithTitle",
			key: "fullNameWithTitle",
		},
		{
			title: "Select ",
			fixed: "right",
			key: "action",
			render: (text, record) => {
				const isDisabledPending = !["active", "pending"].includes(record.state);
				const isDisabledActive = !["active"].includes(record.state);
				const isSelected = currentPatient?.id === record.id;

				return pendingPatientAllowed ? (
					<Space size="middle">
						<Tooltip
							title={
								isDisabledPending &&
								`We found a match but it seems the practice has marked you as inactive. ${location?.phone
									? `Please call them on ${location?.phone} to set you as active.`
									: "Please contact them to set you as active."
								}`
							}
						>
							<Button
								disabled={isDisabledPending}
								type="primary"
								ghost
								style={isDisabledPending ? {} : themedGhostStyle}
								icon={
									isSelected && (
										<CheckCircleOutlined style={{ color: green[4] }} />
									)
								}
								onClick={() => handleSetPatient(record)}
							>
								{!isSelected && "Select"}
							</Button>
						</Tooltip>
					</Space>
				) : (
					<Space size="middle">
						<Tooltip
							title={
								isDisabledActive &&
								`We found a match but it seems the practice has marked you as inactive. ${location?.phone
									? `Please call them on ${location?.phone} to set you as active.`
									: "Please contact them to set you as active."
								}`
							}
						>
							<Button
								icon={
									isSelected && (
										<CheckCircleOutlined style={{ color: green[4] }} />
									)
								}
								disabled={isDisabledActive}
								type="primary"
								ghost
								style={isDisabledActive ? {} : themedGhostStyle}
								onClick={() => handleSetPatient(record)}
							>
								{!isSelected && "Select"}
							</Button>
						</Tooltip>
					</Space>
				);
			},
		},
	];

	return newPatient ? (
		<Row gutter={[12, 12]} justify="space-around">
			<Col flex="650px">
				<NewPatientForm practiceId={practiceId} account={account} />
			</Col>
		</Row>
	) : (
		<>
			<Row justify="space-between">
				<Typography.Title level={4}>Who are you booking for?</Typography.Title>
				<Button
					icon={<UserOutlined />}
					type="primary"
					style={themedButtonStyle}
					onClick={() => {
						trackButtonClick('new_person_button');
						setNewPatient(true);
					}}
				>
					New Person
				</Button>
			</Row>
			<Row gutter={[12, 12]}>
				<Col span={24}>
					<Text type="secondary">
						We detected multiple linked accounts, please select who this
						appointment is for. Alternatively if you need to create another
						account click create new. <br />
					</Text>
				</Col>
				<Col span={24}>
					<Table size="small" dataSource={patients} columns={columns} />
				</Col>
			</Row>
		</>
	);
}

const mapStateToProps = (state) => ({
	patients: getPatients(state.session),
	location: getCurrentLocation(state.locations),
});

export default connect(mapStateToProps, actions)(ExistingPatients);
